/**
 * NIGHTBOUND - the authoritative pet state engine.
 *
 * `calculatePetState` is a *pure* function: given the last stored creature
 * state and the current time, it returns the creature's state now. It is used
 *
 *   * by the server, on every read and every action, to catch up on elapsed
 *     time (offline progression), and
 *   * by the client, purely as a display smoother between server updates.
 *
 * Nothing here depends on the database or on random numbers, so the result is
 * deterministic and fully testable.
 */

import {
  ENERGY,
  HOUR_MS,
  MAX_SIMULATION_MS,
  NEEDS,
  SLEEP,
  SIMULATION_STEP_MS,
  clamp,
  clampNeed,
  round2,
} from './constants'
import { deriveMood } from './mood'
import {
  SPECIES,
  isNaturalActiveTime,
  isPreferredSleepTime,
  localHourOf,
  type MoodId,
  type SpeciesId,
} from './species'

export interface PetStateInput {
  species: SpeciesId
  hunger: number
  energy: number
  hygiene: number
  bond: number
  sanity: number
  fitness: number
  currentHp: number
  maxHp: number
  sleeping: boolean
  sleepStartedAt: Date | null
  lastStateCalculation: Date
  lastInteractionAt: Date | null
  timezoneOffsetMinutes: number
  bondDayKey: string | null
  bondGainToday: number
  xpDayKey: string | null
  xpGainToday: number
  trainingDayKey: string | null
  trainingSessionsToday: number
}

export interface PetStateResult {
  hunger: number
  energy: number
  hygiene: number
  bond: number
  sanity: number
  fitness: number
  currentHp: number
  maxHp: number
  sleeping: boolean
  sleepStartedAt: Date | null
  lastStateCalculation: Date
  bondDayKey: string
  bondGainToday: number
  xpDayKey: string
  xpGainToday: number
  trainingDayKey: string
  trainingSessionsToday: number
  mood: MoodId
  elapsedMs: number
  elapsedHours: number
  wokeUp: boolean
  wokeUpReason: 'ENERGY' | 'DURATION' | null
  collapsed: boolean
  /** True when the creature is trying to sleep during its preferred window. */
  inSleepWindow: boolean
  inActiveWindow: boolean
  /** Set when the simulation was clipped at MAX_SIMULATION_MS. */
  clipped: boolean
}

/** Local calendar day key, e.g. "2026-10-14", in the player's timezone. */
export function dayKeyFor(date: Date, timezoneOffsetMinutes: number): string {
  const local = new Date(date.getTime() + timezoneOffsetMinutes * 60_000)
  const y = local.getUTCFullYear()
  const m = String(local.getUTCMonth() + 1).padStart(2, '0')
  const d = String(local.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function hoursBetween(from: Date, to: Date): number {
  return Math.max(0, (to.getTime() - from.getTime()) / HOUR_MS)
}

/**
 * Catch the creature up to `now`.
 *
 * Time is integrated in fixed steps rather than solved analytically so that
 * thresholds (energy full, needs empty, sleep auto-wake) behave correctly for
 * arbitrary gaps - including a player returning after a month.
 */
export function calculatePetState(input: PetStateInput, now: Date): PetStateResult {
  const species = SPECIES[input.species]
  const tz = input.timezoneOffsetMinutes

  const elapsedRawMs = Math.max(0, now.getTime() - input.lastStateCalculation.getTime())
  const elapsedMs = Math.min(elapsedRawMs, MAX_SIMULATION_MS)
  const clipped = elapsedRawMs > MAX_SIMULATION_MS

  let hunger = clampNeed(input.hunger)
  let energy = clampNeed(input.energy)
  let hygiene = clampNeed(input.hygiene)
  let bond = clampNeed(input.bond)
  let sanity = clampNeed(input.sanity)
  let fitness = clampNeed(input.fitness)
  let currentHp = clamp(input.currentHp, NEEDS.minimumHp, input.maxHp)

  let sleeping = input.sleeping
  let sleepStartedAt = input.sleepStartedAt
  let wokeUp = false
  let wokeUpReason: PetStateResult['wokeUpReason'] = null
  let collapsed = false

  const todayKey = dayKeyFor(now, tz)

  // Daily budgets reset on the player's local day boundary.
  let bondGainToday = input.bondDayKey === todayKey ? input.bondGainToday : 0
  let xpGainToday = input.xpDayKey === todayKey ? input.xpGainToday : 0
  let trainingSessionsToday =
    input.trainingDayKey === todayKey ? input.trainingSessionsToday : 0

  // A sleeping creature with no recorded start time is treated as having just
  // gone to sleep rather than sleeping for the entire elapsed window.
  if (sleeping && !sleepStartedAt) sleepStartedAt = input.lastStateCalculation

  const steps = Math.ceil(elapsedMs / SIMULATION_STEP_MS)
  const stepMs = steps > 0 ? elapsedMs / steps : 0
  const stepHours = stepMs / HOUR_MS

  for (let i = 0; i < steps; i += 1) {
    const stepTime = new Date(input.lastStateCalculation.getTime() + stepMs * (i + 1))

    const inSleepWindow = isPreferredSleepTime(species, stepTime, tz)

    if (sleeping) {
      // ---- resting -----------------------------------------------------
      // Sleep returns energy fast (+1/minute), more in the natural window.
      let recovery = ENERGY.sleepRegenPerHour * species.modifiers.sleepRecovery
      if (inSleepWindow) recovery *= ENERGY.preferredWindowMultiplier
      // A starving creature cannot rest properly.
      const restQuality = hunger < SLEEP.hungrySleepThreshold ? SLEEP.hungrySleepPenalty : 1
      energy = clampNeed(energy + recovery * restQuality * stepHours)

      // The natural sleep window is doubly restorative: sanity recovers 25%
      // faster there too.
      const sanityRecovery =
        NEEDS.sanityRecoveryPerHour * (inSleepWindow ? ENERGY.preferredWindowMultiplier : 1)
      sanity = clampNeed(sanity + sanityRecovery * stepHours * restQuality)
      // Hunger and hygiene still slip while asleep, but more slowly.
      hunger = clampNeed(
        hunger -
          NEEDS.hungerDecayPerHour *
            species.modifiers.hungerDecay *
            0.55 *
            stepHours,
      )
      hygiene = clampNeed(
        hygiene -
          NEEDS.hygieneDecayPerHour * species.modifiers.hygieneDecay * 0.4 * stepHours,
      )

      const sleptHours = sleepStartedAt ? hoursBetween(sleepStartedAt, stepTime) : 0
      if (energy >= SLEEP.autoWakeEnergy) {
        sleeping = false
        sleepStartedAt = null
        wokeUp = true
        wokeUpReason = 'ENERGY'
      } else if (sleptHours >= SLEEP.maxSleepHours) {
        sleeping = false
        sleepStartedAt = null
        wokeUp = true
        wokeUpReason = 'DURATION'
      }
    } else {
      // ---- awake -------------------------------------------------------
      // Passive recovery: +1 energy every 5 minutes. Spending energy (play,
      // training) far outpaces this, so sleep is what actually refills it.
      energy = clampNeed(energy + ENERGY.awakeRegenPerHour * stepHours)

      hunger = clampNeed(
        hunger - NEEDS.hungerDecayPerHour * species.modifiers.hungerDecay * stepHours,
      )
      hygiene = clampNeed(
        hygiene - NEEDS.hygieneDecayPerHour * species.modifiers.hygieneDecay * stepHours,
      )

      sanity = clampNeed(sanity - NEEDS.sanityDriftPerHour * stepHours)
      if (hunger < 25) sanity = clampNeed(sanity - NEEDS.sanityPenaltyLowHunger * stepHours)
      if (hygiene < 25) sanity = clampNeed(sanity - NEEDS.sanityPenaltyLowHygiene * stepHours)
      if (bond >= 70) sanity = clampNeed(sanity + NEEDS.sanityBonusHighBond * stepHours)

      const neglect =
        hunger < 20 || hygiene < 20 || energy < 15
          ? NEEDS.bondNeglectPerHour
          : 0
      if (neglect > 0) bond = clampNeed(bond - neglect * stepHours)

      // Being alone for days erodes the bond even if the needs were topped up.
      const awayHours = input.lastInteractionAt
        ? (stepTime.getTime() - input.lastInteractionAt.getTime()) / HOUR_MS
        : 0
      if (awayHours > NEEDS.bondAbandonmentAfterHours) {
        bond = clampNeed(bond - NEEDS.bondAbandonmentPerHour * stepHours)
      }

      // Exhaustion collapse: the creature simply passes out.
      if (energy <= SLEEP.collapseEnergy) {
        sleeping = true
        sleepStartedAt = stepTime
        collapsed = true
      }
    }

    // ---- health ---------------------------------------------------------
    if (hunger <= 0 && hygiene < 10) {
      currentHp = Math.max(
        NEEDS.minimumHp,
        currentHp - NEEDS.starvingHpLossPerHour * stepHours,
      )
    } else if (hunger >= NEEDS.naturalHpRegenMinHunger && hygiene >= NEEDS.naturalHpRegenMinHygiene) {
      currentHp = Math.min(input.maxHp, currentHp + NEEDS.naturalHpRegenPerHour * stepHours)
    }
  }

  const finalTime = new Date(input.lastStateCalculation.getTime() + elapsedMs)
  const inSleepWindow = isPreferredSleepTime(species, now, tz)
  const inActiveWindow = isNaturalActiveTime(species, now, tz)

  const result: PetStateResult = {
    hunger: round2(hunger),
    energy: round2(energy),
    hygiene: round2(hygiene),
    bond: round2(bond),
    sanity: round2(sanity),
    fitness: round2(fitness),
    currentHp: round2(currentHp),
    maxHp: input.maxHp,
    sleeping,
    sleepStartedAt,
    lastStateCalculation: finalTime,
    bondDayKey: todayKey,
    bondGainToday: round2(bondGainToday),
    xpDayKey: todayKey,
    xpGainToday: round2(xpGainToday),
    trainingDayKey: todayKey,
    trainingSessionsToday,
    mood: 'CONTENT',
    elapsedMs,
    elapsedHours: round2(elapsedMs / HOUR_MS),
    wokeUp,
    wokeUpReason,
    collapsed,
    inSleepWindow,
    inActiveWindow,
    clipped,
  }

  result.mood = deriveMood(result)
  return result
}

/** Convenience for the client's display smoother. */
export function projectNeeds(
  state: Pick<
    PetStateResult,
    'hunger' | 'energy' | 'hygiene' | 'bond' | 'sanity'
  >,
  hours: number,
): Pick<PetStateResult, 'hunger' | 'energy' | 'hygiene' | 'bond' | 'sanity'> {
  return {
    hunger: clampNeed(state.hunger - NEEDS.hungerDecayPerHour * hours),
    // Awake energy now trickles *up* slowly (see ENERGY.awakeRegenPerHour).
    energy: clampNeed(state.energy + ENERGY.awakeRegenPerHour * hours),
    hygiene: clampNeed(state.hygiene - NEEDS.hygieneDecayPerHour * hours),
    bond: state.bond,
    sanity: clampNeed(state.sanity - NEEDS.sanityDriftPerHour * hours),
  }
}

/** Exposed for tests and debug tooling. */
export const PET_STATE_INTERNALS = {
  localHourOf,
  SIMULATION_STEP_MS,
}
